import React, { useState, useEffect, useCallback } from 'react';
import {
  listScenarios,
  getScenario,
  createScenario,
  updateScenario,
  deleteScenario,
  listDirectQuestions,
  createDirectQuestion,
  updateDirectQuestion,
  deleteDirectQuestion,
} from '../services/scenarioApi';
import {
  PROCESS_LABELS,
  ALL_PROCESSES,
  processesForPurpose,
  PURPOSES,
  FILTER_PURPOSES,
  PURPOSE_LABELS,
} from '../constants/scenarioConstants';
import './ScenarioManagement.css';

const PROCESS_SESSION_KEY = 'scenarioMgmt.selectedProcess';
const PURPOSE_SESSION_KEY = 'scenarioMgmt.selectedPurpose';
const SCENARIO_TYPES = ['billing', 'sales', 'retention', 'service'];
// New "Scenario Type" field (distinct from the legacy scenario_type/process-type).
const SCENARIO_CATEGORIES = ['complaint', 'concern', 'query', 'na'];
const SCENARIO_CATEGORY_LABELS = { complaint: 'Complaint', concern: 'Concern', query: 'Query', na: 'NA' };
const MAX_QUESTIONS = 20;
const MIN_QUESTIONS = 1;

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

// Build an empty form model for a new scenario.
export function emptyScenarioForm() {
  return {
    title: '',
    description: '',
    scenario_type: 'billing',
    scenario_category: '',
    context: '',
    guidelines: '',
    reading_time_minutes: 5,
    persona: {
      name: '',
      emotional_state: '',
      personality: [],
      objectives: [],
    },
    prohibited_behaviours: [],
    reference_transcripts: [],
    questions: [{ question_text: '', order: 1 }],
  };
}

// Convert a fetched scenario into the editable form model (faithful round-trip).
export function scenarioToForm(sc) {
  const persona = sc.persona || {};
  const questions = Array.isArray(sc.questions) ? [...sc.questions] : [];
  questions.sort((a, b) => (a.order || 0) - (b.order || 0));
  return {
    title: sc.title || '',
    description: sc.description || '',
    scenario_type: sc.scenario_type || 'billing',
    scenario_category: (sc.scenario_category || '').toLowerCase(),
    context: sc.context || '',
    guidelines: sc.guidelines || '',
    reading_time_minutes:
      sc.reading_time_minutes === undefined || sc.reading_time_minutes === null
        ? 5
        : sc.reading_time_minutes,
    persona: {
      name: persona.name || '',
      emotional_state: persona.emotional_state || '',
      personality: Array.isArray(persona.personality) ? [...persona.personality] : [],
      objectives: Array.isArray(persona.objectives) ? [...persona.objectives] : [],
    },
    prohibited_behaviours: Array.isArray(sc.prohibited_behaviours)
      ? [...sc.prohibited_behaviours]
      : [],
    reference_transcripts: Array.isArray(sc.reference_transcripts)
      ? [...sc.reference_transcripts]
      : [],
    questions:
      questions.length > 0
        ? questions.map((q, i) => ({ question_text: q.question_text || '', order: i + 1 }))
        : [{ question_text: '', order: 1 }],
  };
}

// Re-assign contiguous 1..n ordering to a questions array.
export function reorderQuestions(questions) {
  return questions.map((q, i) => ({ ...q, order: i + 1 }));
}

// Build the request body the backend expects (client omits server-generated IDs).
export function formToBody(form) {
  return {
    title: form.title.trim(),
    description: form.description.trim(),
    scenario_type: form.scenario_type,
    scenario_category: form.scenario_category,
    context: form.context.trim(),
    guidelines: form.guidelines.trim(),
    reading_time_minutes: Number(form.reading_time_minutes),
    persona: {
      name: form.persona.name.trim(),
      emotional_state: form.persona.emotional_state.trim(),
      personality: form.persona.personality,
      objectives: form.persona.objectives,
    },
    prohibited_behaviours: form.prohibited_behaviours,
    reference_transcripts: form.reference_transcripts,
    questions: reorderQuestions(form.questions).map((q) => ({
      question_text: q.question_text.trim(),
      order: q.order,
    })),
  };
}

// Validate the scenario form; returns a map of fieldKey -> error message.
// Purpose is validated separately in the component (formPurpose is not part of
// the form model), so this helper is unchanged by the purpose feature.
export function validateScenarioForm(form) {
  const errors = {};
  if (!form.title.trim()) errors.title = 'Title is required';
  else if (form.title.length > 200) errors.title = 'Title must be 200 characters or fewer';

  if (!form.description.trim()) errors.description = 'Description is required';
  else if (form.description.length > 2000)
    errors.description = 'Description must be 2000 characters or fewer';

  if (!SCENARIO_TYPES.includes(form.scenario_type))
    errors.scenario_type = 'Select a valid scenario type';

  if (!SCENARIO_CATEGORIES.includes(form.scenario_category))
    errors.scenario_category = 'Please select a scenario type';

  if (!form.context.trim()) errors.context = 'Context is required';
  else if (form.context.length > 5000)
    errors.context = 'Context must be 5000 characters or fewer';

  if (form.guidelines && form.guidelines.length > 100000)
    errors.guidelines = 'Guidelines is too long';

  const rt = Number(form.reading_time_minutes);
  if (!Number.isInteger(rt) || rt < 1 || rt > 60)
    errors.reading_time_minutes = 'Reading time must be a whole number between 1 and 60';

  if (!form.persona.name.trim()) errors['persona.name'] = 'Persona name is required';

  if (form.questions.length < MIN_QUESTIONS)
    errors.questions = 'At least one question is required';
  else if (form.questions.length > MAX_QUESTIONS)
    errors.questions = `No more than ${MAX_QUESTIONS} questions are allowed`;

  const questionErrors = {};
  form.questions.forEach((q, i) => {
    if (!q.question_text.trim()) questionErrors[i] = 'Question text is required';
    else if (q.question_text.length > 1000)
      questionErrors[i] = 'Question must be 1000 characters or fewer';
  });
  if (Object.keys(questionErrors).length > 0) errors.questionItems = questionErrors;

  return errors;
}

/* ------------------------------------------------------------------ */
/* Reusable confirm dialog                                            */
/* ------------------------------------------------------------------ */

function ConfirmDialog({ title, message, confirmLabel = 'Confirm', onConfirm, onCancel }) {
  return (
    <div className="sm-modal-overlay" onClick={onCancel}>
      <div
        className="sm-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sm-modal-header">
          <h3>{title}</h3>
          <button className="sm-modal-close" aria-label="Close dialog" onClick={onCancel}>
            ✕
          </button>
        </div>
        <p className="sm-modal-body">{message}</p>
        <div className="sm-modal-actions">
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn-action btn-danger" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Simple list-of-strings editor (chips + add input)                  */
/* ------------------------------------------------------------------ */

function StringListEditor({ label, items, onChange, placeholder }) {
  const [draft, setDraft] = useState('');

  const addItem = () => {
    const value = draft.trim();
    if (!value) return;
    onChange([...items, value]);
    setDraft('');
  };

  const removeItem = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="sm-chip-input-row">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Type a value and press Add'}
          aria-label={`Add ${label}`}
        />
        <button type="button" className="btn-chip-add" onClick={addItem}>
          Add
        </button>
      </div>
      {items.length > 0 && (
        <div className="sm-chips">
          {items.map((item, idx) => (
            <span className="sm-chip" key={`${item}-${idx}`}>
              {item}
              <button
                type="button"
                className="sm-chip-remove"
                aria-label={`Remove ${item}`}
                onClick={() => removeItem(idx)}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Session-storage helpers                                            */
/* ------------------------------------------------------------------ */

function readPersistedProcess() {
  try {
    const value = sessionStorage.getItem(PROCESS_SESSION_KEY) || '';
    if (value && !ALL_PROCESSES.includes(value)) {
      // Stale persisted value (process no longer supported): clear it.
      sessionStorage.removeItem(PROCESS_SESSION_KEY);
      return '';
    }
    return value;
  } catch {
    return '';
  }
}

function readPersistedPurpose() {
  try {
    const value = sessionStorage.getItem(PURPOSE_SESSION_KEY) || '';
    if (value && !FILTER_PURPOSES.includes(value)) {
      // Stale persisted value (purpose no longer a valid filter): clear it.
      sessionStorage.removeItem(PURPOSE_SESSION_KEY);
      return '';
    }
    return value;
  } catch {
    return '';
  }
}

/* ------------------------------------------------------------------ */
/* Main page                                                          */
/* ------------------------------------------------------------------ */

export default function ScenarioManagement() {
  // Process + purpose filter selection. Process is now a fixed closed set,
  // so there is no discovery fetch — the dropdown is populated directly from
  // processesForPurpose(selectedPurpose).
  const [selectedProcess, setSelectedProcess] = useState(readPersistedProcess);
  const [selectedPurpose, setSelectedPurpose] = useState(readPersistedPurpose);

  // View state machine: 'list' | 'detail' | 'create' | 'edit'
  const [view, setView] = useState('list');

  // Scenario list
  const [scenarios, setScenarios] = useState([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [scenariosError, setScenariosError] = useState('');

  // Detail
  const [detailScenario, setDetailScenario] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Form (create/edit)
  const [form, setForm] = useState(emptyScenarioForm());
  const [formErrors, setFormErrors] = useState({});
  const [editingScenarioId, setEditingScenarioId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Purpose chosen in the create/edit form (training|hiring). Always starts
  // blank and must be explicitly picked (blank-must-pick). This is separate
  // from the list filter selectedPurpose.
  const [formPurpose, setFormPurpose] = useState('');
  const [formPurposeError, setFormPurposeError] = useState('');

  // Delete scenario confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Direct questions
  const [directQuestions, setDirectQuestions] = useState([]);
  const [dqLoading, setDqLoading] = useState(false);
  const [dqError, setDqError] = useState('');
  const [dqDraft, setDqDraft] = useState('');
  const [dqDraftError, setDqDraftError] = useState('');
  // Purpose chosen for adding a direct question (training|hiring, blank-must-pick).
  const [directFormPurpose, setDirectFormPurpose] = useState('');
  const [directFormPurposeError, setDirectFormPurposeError] = useState('');
  const [dqEditingId, setDqEditingId] = useState('');
  const [dqEditText, setDqEditText] = useState('');
  const [dqEditPurpose, setDqEditPurpose] = useState('');
  const [dqEditPurposeError, setDqEditPurposeError] = useState('');
  const [dqDeleteTarget, setDqDeleteTarget] = useState(null);

  // Global notifications
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Both a process and a purpose must be selected before any list is fetched.
  const ready = !!selectedProcess && !!selectedPurpose;
  // Unclassified is a read-only filter: nothing can be created into it.
  const createDisabled = selectedPurpose === 'unclassified';

  /* ---------------- Load content for the current filter ---------------- */

  const loadScenarios = useCallback(async (proc, purpose) => {
    if (!proc || !purpose) return;
    // Clear the previous list immediately so no stale data is shown mid-fetch.
    setScenarios([]);
    setScenariosError('');
    setScenariosLoading(true);
    try {
      const data = await listScenarios(proc, purpose);
      setScenarios(Array.isArray(data) ? data : []);
    } catch (err) {
      setScenariosError(err.message || 'Failed to load scenarios');
      setScenarios([]); // never leave a partial/stale list on error
    } finally {
      setScenariosLoading(false);
    }
  }, []);

  const loadDirectQuestions = useCallback(async (proc, purpose) => {
    if (!proc || !purpose) return;
    setDirectQuestions([]);
    setDqError('');
    setDqLoading(true);
    try {
      const data = await listDirectQuestions(proc, purpose);
      setDirectQuestions(Array.isArray(data) ? data : []);
    } catch (err) {
      setDqError(err.message || 'Failed to load direct questions');
      setDirectQuestions([]);
    } finally {
      setDqLoading(false);
    }
  }, []);

  // When either the selected process or purpose changes, reload BOTH lists for
  // the new (process, purpose) combination. If either is unset, clear the lists
  // and fetch nothing.
  useEffect(() => {
    if (!selectedProcess || !selectedPurpose) {
      setScenarios([]);
      setDirectQuestions([]);
      return;
    }
    setView('list');
    loadScenarios(selectedProcess, selectedPurpose);
    loadDirectQuestions(selectedProcess, selectedPurpose);
  }, [selectedProcess, selectedPurpose, loadScenarios, loadDirectQuestions]);

  /* ---------------- Filter selection handlers ---------------- */

  const persist = (key, value) => {
    try {
      if (value) sessionStorage.setItem(key, value);
      else sessionStorage.removeItem(key);
    } catch {
      /* ignore storage failures */
    }
  };

  const handleSelectProcess = (proc) => {
    setMessage('');
    setError('');
    setSelectedProcess(proc);
    persist(PROCESS_SESSION_KEY, proc);
  };

  const handleSelectPurpose = (purpose) => {
    setMessage('');
    setError('');
    setSelectedPurpose(purpose);
    persist(PURPOSE_SESSION_KEY, purpose);
    // The process list depends on the purpose; drop a process that is not
    // valid for the newly selected purpose so a stale value cannot persist.
    if (selectedProcess && !processesForPurpose(purpose).includes(selectedProcess)) {
      setSelectedProcess('');
      persist(PROCESS_SESSION_KEY, '');
    }
  };

  /* ---------------- Detail view ---------------- */

  const openDetail = async (scenarioId) => {
    if (!ready) return;
    setView('detail');
    setDetailScenario(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const data = await getScenario(selectedProcess, selectedPurpose, scenarioId);
      setDetailScenario(data);
    } catch (err) {
      setDetailError(err.message || 'Failed to load scenario');
    } finally {
      setDetailLoading(false);
    }
  };

  /* ---------------- Create / edit ---------------- */

  const openCreate = () => {
    if (createDisabled) return;
    setForm(emptyScenarioForm());
    setFormErrors({});
    // Auto-fill the form purpose from the active top-filter purpose. Create is
    // blocked under the 'unclassified' filter (createDisabled), so at this point
    // selectedPurpose is always a concrete purpose (training|hiring).
    setFormPurpose(PURPOSES.includes(selectedPurpose) ? selectedPurpose : '');
    setFormPurposeError('');
    setEditingScenarioId('');
    setMessage('');
    setError('');
    setView('create');
  };

  const openEdit = async (scenarioId) => {
    if (!ready) return;
    setMessage('');
    setError('');
    setFormErrors({});
    setFormPurpose(''); // edit form always opens blank regardless of stored purpose
    setFormPurposeError('');
    setEditingScenarioId(scenarioId);
    setView('edit');
    setDetailLoading(true);
    try {
      const data = await getScenario(selectedProcess, selectedPurpose, scenarioId);
      setForm(scenarioToForm(data));
    } catch (err) {
      setError(err.message || 'Failed to load scenario for editing');
      setView('list');
    } finally {
      setDetailLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updatePersonaField = (key, value) => {
    setForm((prev) => ({ ...prev, persona: { ...prev.persona, [key]: value } }));
  };

  // Question operations
  const addQuestion = () => {
    setForm((prev) => {
      if (prev.questions.length >= MAX_QUESTIONS) return prev;
      return {
        ...prev,
        questions: reorderQuestions([...prev.questions, { question_text: '', order: 0 }]),
      };
    });
  };

  const removeQuestion = (idx) => {
    setForm((prev) => {
      if (prev.questions.length <= MIN_QUESTIONS) return prev;
      return {
        ...prev,
        questions: reorderQuestions(prev.questions.filter((_, i) => i !== idx)),
      };
    });
  };

  const moveQuestion = (idx, dir) => {
    setForm((prev) => {
      const target = idx + dir;
      if (target < 0 || target >= prev.questions.length) return prev;
      const next = [...prev.questions];
      const tmp = next[idx];
      next[idx] = next[target];
      next[target] = tmp;
      return { ...prev, questions: reorderQuestions(next) };
    });
  };

  const updateQuestionText = (idx, text) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === idx ? { ...q, question_text: text } : q)),
    }));
  };

  const handleSubmitScenario = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    // Purpose is validated in the component (blank-must-pick) in addition to the
    // field-level form validation.
    const errors = validateScenarioForm(form);
    const purposeMissing = !PURPOSES.includes(formPurpose);
    setFormPurposeError(purposeMissing ? 'Please select a purpose' : '');
    if (Object.keys(errors).length > 0 || purposeMissing) {
      setFormErrors(errors);
      return; // block submission, send no request
    }
    setFormErrors({});
    setSubmitting(true);
    const body = formToBody(form);
    try {
      if (view === 'edit') {
        // The form purpose is the stored purpose — this is what reclassifies a
        // document (e.g. an unclassified doc being set to training/hiring).
        await updateScenario(selectedProcess, formPurpose, editingScenarioId, body);
        setMessage('Scenario updated successfully');
      } else {
        await createScenario(selectedProcess, formPurpose, body);
        setMessage('Scenario created successfully');
      }
      // Refresh the list for the ACTIVE filter (selectedProcess, selectedPurpose).
      // A reclassified doc will drop out of the current filter view if its new
      // purpose differs from the filter (e.g. leaving the Unclassified list).
      await loadScenarios(selectedProcess, selectedPurpose);
      setView('list');
    } catch (err) {
      setError(err.message || 'Failed to save scenario');
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- Delete scenario ---------------- */

  const confirmDeleteScenario = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    setMessage('');
    setError('');
    try {
      // Delete accepts any filter purpose, including unclassified.
      await deleteScenario(selectedProcess, selectedPurpose, target.scenario_id);
      setMessage(`Scenario "${target.title || target.scenario_id}" deleted`);
      await loadScenarios(selectedProcess, selectedPurpose);
    } catch (err) {
      // Leave the list unchanged on failure.
      setError(err.message || 'Failed to delete scenario');
    }
  };

  /* ---------------- Direct questions ---------------- */

  const handleAddDirectQuestion = async (e) => {
    e.preventDefault();
    setDqDraftError('');
    setDirectFormPurposeError('');
    setMessage('');
    setError('');
    if (createDisabled) return;
    const text = dqDraft.trim();
    const purposeMissing = !PURPOSES.includes(directFormPurpose);
    if (!text) {
      setDqDraftError('Question text is required');
    } else if (text.length > 2000) {
      setDqDraftError('Question must be 2000 characters or fewer');
    }
    if (purposeMissing) {
      setDirectFormPurposeError('Please select a purpose');
    }
    if (!text || text.length > 2000 || purposeMissing) {
      return; // block submission, send no request
    }
    try {
      // Direct-question create stores the explicitly picked form purpose.
      await createDirectQuestion(selectedProcess, directFormPurpose, { question_text: text });
      setDqDraft('');
      setDirectFormPurpose('');
      setMessage('Direct question added');
      await loadDirectQuestions(selectedProcess, selectedPurpose);
    } catch (err) {
      setError(err.message || 'Failed to add direct question');
    }
  };

  const startEditDirectQuestion = (dq) => {
    setDqEditingId(dq.question_id);
    setDqEditText(dq.question_text || '');
    // If the filter is a concrete purpose (training|hiring) use it as the edit
    // purpose; under the unclassified filter the superadmin must pick one.
    setDqEditPurpose(PURPOSES.includes(selectedPurpose) ? selectedPurpose : '');
    setDqEditPurposeError('');
  };

  const cancelEditDirectQuestion = () => {
    setDqEditingId('');
    setDqEditText('');
    setDqEditPurpose('');
    setDqEditPurposeError('');
  };

  const saveEditDirectQuestion = async (questionId) => {
    setMessage('');
    setError('');
    setDqEditPurposeError('');
    const text = dqEditText.trim();
    if (!text) {
      setError('Question text is required');
      return;
    }
    const purposeMissing = !PURPOSES.includes(dqEditPurpose);
    if (purposeMissing) {
      setDqEditPurposeError('Please select a purpose');
      return;
    }
    try {
      await updateDirectQuestion(selectedProcess, dqEditPurpose, questionId, {
        question_text: text,
      });
      setMessage('Direct question updated');
      cancelEditDirectQuestion();
      await loadDirectQuestions(selectedProcess, selectedPurpose);
    } catch (err) {
      setError(err.message || 'Failed to update direct question');
    }
  };

  const confirmDeleteDirectQuestion = async () => {
    const target = dqDeleteTarget;
    setDqDeleteTarget(null);
    if (!target) return;
    setMessage('');
    setError('');
    try {
      await deleteDirectQuestion(selectedProcess, selectedPurpose, target.question_id);
      setMessage('Direct question deleted');
      await loadDirectQuestions(selectedProcess, selectedPurpose);
    } catch (err) {
      setError(err.message || 'Failed to delete direct question');
    }
  };

  /* ---------------- Render helpers ---------------- */

  const renderFilterBar = () => (
    <div className="sm-process-bar">
      <div className="sm-process-select-group">
        <label htmlFor="sm-purpose-select">Purpose</label>
        <select
          id="sm-purpose-select"
          className="sm-process-select"
          value={selectedPurpose}
          onChange={(e) => handleSelectPurpose(e.target.value)}
        >
          <option value="">— Select a purpose —</option>
          {FILTER_PURPOSES.map((p) => (
            <option key={p} value={p}>
              {PURPOSE_LABELS[p] || p}
            </option>
          ))}
        </select>
      </div>

      <div className="sm-process-select-group">
        <label htmlFor="sm-process-select">Process</label>
        <select
          id="sm-process-select"
          className="sm-process-select"
          value={selectedProcess}
          onChange={(e) => handleSelectProcess(e.target.value)}
          disabled={!selectedPurpose}
        >
          <option value="">
            {selectedPurpose ? '— Select a process —' : '— Select a purpose first —'}
          </option>
          {processesForPurpose(selectedPurpose).map((p) => (
            <option key={p} value={p}>
              {PROCESS_LABELS[p] || p}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderScenarioList = () => (
    <div className="mgmt-card">
      <div className="sm-section-head">
        <h3>Scenarios</h3>
        {!createDisabled && (
          <button className="btn-add-user" onClick={openCreate}>
            + Add Scenario
          </button>
        )}
      </div>

      {createDisabled && (
        <p className="sm-unclassified-note">
          Select Training or Hiring to add new items.
        </p>
      )}

      {scenariosLoading ? (
        <p className="mgmt-loading">Loading scenarios…</p>
      ) : scenariosError ? (
        <div className="sm-inline-error">
          <span>{scenariosError}</span>
          <button
            className="btn-back"
            onClick={() => loadScenarios(selectedProcess, selectedPurpose)}
          >
            Retry
          </button>
        </div>
      ) : scenarios.length === 0 ? (
        <p className="mgmt-empty">
          No scenarios yet for this process and purpose.
          {!createDisabled ? ' Click "+ Add Scenario" to create one.' : ''}
        </p>
      ) : (
        <div className="sm-scenario-list">
          {scenarios.map((sc) => (
            <div className="sm-scenario-row" key={sc.scenario_id}>
              <div className="sm-scenario-main">
                <span className="sm-scenario-id">{sc.scenario_id}</span>
                <span className="sm-scenario-title">{sc.title}</span>
              </div>
              <div className="sm-scenario-meta">
                <span className={`sm-type-badge sm-type-${sc.scenario_type}`}>
                  {sc.scenario_type}
                </span>
                <span className="sm-meta-item">Persona: {sc.persona?.name || '—'}</span>
                <span className="sm-meta-item">
                  {Array.isArray(sc.questions) ? sc.questions.length : 0} question
                  {(Array.isArray(sc.questions) ? sc.questions.length : 0) === 1 ? '' : 's'}
                </span>
                <span className="sm-meta-item">{sc.reading_time_minutes} min read</span>
              </div>
              <div className="sm-scenario-actions">
                <button className="btn-back" onClick={() => openDetail(sc.scenario_id)}>
                  View
                </button>
                <button className="btn-back" onClick={() => openEdit(sc.scenario_id)}>
                  Edit
                </button>
                <button className="btn-remove" onClick={() => setDeleteTarget(sc)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderDetail = () => (
    <div className="mgmt-card">
      <div className="sm-section-head">
        <h3>Scenario Detail</h3>
        <button className="btn-back" onClick={() => setView('list')}>
          ← Back to list
        </button>
      </div>

      {detailLoading ? (
        <p className="mgmt-loading">Loading scenario…</p>
      ) : detailError ? (
        <div className="sm-inline-error">
          <span>{detailError}</span>
        </div>
      ) : detailScenario ? (
        <div className="sm-detail">
          <div className="sm-detail-field">
            <span className="sm-detail-label">Scenario ID</span>
            <span className="sm-detail-value">{detailScenario.scenario_id}</span>
          </div>
          <div className="sm-detail-field">
            <span className="sm-detail-label">Title</span>
            <span className="sm-detail-value">{detailScenario.title}</span>
          </div>
          <div className="sm-detail-field">
            <span className="sm-detail-label">Description</span>
            <span className="sm-detail-value">{detailScenario.description}</span>
          </div>
          <div className="sm-detail-field">
            <span className="sm-detail-label">Type</span>
            <span className="sm-detail-value">{detailScenario.scenario_type}</span>
          </div>
          <div className="sm-detail-field">
            <span className="sm-detail-label">Scenario Type</span>
            <span className="sm-detail-value">
              {SCENARIO_CATEGORY_LABELS[(detailScenario.scenario_category || 'na').toLowerCase()] || detailScenario.scenario_category}
            </span>
          </div>
          <div className="sm-detail-field">
            <span className="sm-detail-label">Reading time</span>
            <span className="sm-detail-value">{detailScenario.reading_time_minutes} min</span>
          </div>

          <div className="sm-detail-subsection">
            <h4>Persona</h4>
            <div className="sm-detail-field">
              <span className="sm-detail-label">Name</span>
              <span className="sm-detail-value">{detailScenario.persona?.name || '—'}</span>
            </div>
            <div className="sm-detail-field">
              <span className="sm-detail-label">Personality</span>
              <span className="sm-detail-value">
                {(detailScenario.persona?.personality || []).join(', ') || '—'}
              </span>
            </div>
            <div className="sm-detail-field">
              <span className="sm-detail-label">Emotional state</span>
              <span className="sm-detail-value">
                {detailScenario.persona?.emotional_state || '—'}
              </span>
            </div>
            <div className="sm-detail-field">
              <span className="sm-detail-label">Objectives</span>
              <span className="sm-detail-value">
                {(detailScenario.persona?.objectives || []).join(', ') || '—'}
              </span>
            </div>
          </div>

          <div className="sm-detail-field">
            <span className="sm-detail-label">Context</span>
            <span className="sm-detail-value">{detailScenario.context}</span>
          </div>
          <div className="sm-detail-field">
            <span className="sm-detail-label">Guidelines</span>
            <span className="sm-detail-value">{detailScenario.guidelines || '—'}</span>
          </div>
          <div className="sm-detail-field">
            <span className="sm-detail-label">Prohibited behaviours</span>
            <span className="sm-detail-value">
              {(detailScenario.prohibited_behaviours || []).join(', ') || '—'}
            </span>
          </div>
          <div className="sm-detail-field">
            <span className="sm-detail-label">Reference transcripts</span>
            <span className="sm-detail-value">
              {(detailScenario.reference_transcripts || []).join(', ') || '—'}
            </span>
          </div>

          <div className="sm-detail-subsection">
            <h4>Questions</h4>
            <ol className="sm-detail-questions">
              {[...(detailScenario.questions || [])]
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((q) => (
                  <li key={q.question_id || q.order}>
                    <span className="sm-q-id">{q.question_id}</span>
                    <span className="sm-q-order">#{q.order}</span>
                    <span className="sm-q-text">{q.question_text}</span>
                  </li>
                ))}
            </ol>
          </div>
        </div>
      ) : null}
    </div>
  );

  const renderForm = () => {
    const qErrors = formErrors.questionItems || {};
    return (
      <div className="mgmt-card">
        <div className="sm-section-head">
          <h3>{view === 'edit' ? 'Edit Scenario' : 'Create Scenario'}</h3>
          <button className="btn-back" onClick={() => setView('list')}>
            ← Cancel
          </button>
        </div>

        {view === 'edit' && (
          <p className="sm-immutable-note">
            Scenario ID: <strong>{editingScenarioId}</strong> (cannot be changed)
          </p>
        )}

        {detailLoading ? (
          <p className="mgmt-loading">Loading…</p>
        ) : (
          <form className="mgmt-form" onSubmit={handleSubmitScenario} noValidate>
            <div className="form-group">
              <label htmlFor="sm-title">Title *</label>
              <input
                id="sm-title"
                type="text"
                maxLength={200}
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
              />
              {formErrors.title && <span className="sm-field-error">{formErrors.title}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="sm-description">Description *</label>
              <textarea
                id="sm-description"
                maxLength={2000}
                rows={3}
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
              />
              {formErrors.description && (
                <span className="sm-field-error">{formErrors.description}</span>
              )}
            </div>

            <div className="sm-form-row">
              <div className="form-group">
                <label htmlFor="sm-process-display">Process *</label>
                <input
                  id="sm-process-display"
                  type="text"
                  value={PROCESS_LABELS[selectedProcess] || selectedProcess}
                  readOnly
                  disabled
                />
              </div>

              <div className="form-group">
                <label htmlFor="sm-form-purpose">Purpose *</label>
                <select
                  id="sm-form-purpose"
                  value={formPurpose}
                  onChange={(e) => {
                    setFormPurpose(e.target.value);
                    if (e.target.value) setFormPurposeError('');
                  }}
                >
                  <option value="">— Select a purpose —</option>
                  {PURPOSES.map((p) => (
                    <option key={p} value={p}>
                      {PURPOSE_LABELS[p] || p}
                    </option>
                  ))}
                </select>
                {formPurposeError && (
                  <span className="sm-field-error">{formPurposeError}</span>
                )}
              </div>
            </div>

            <div className="sm-form-row">
              <div className="form-group">
                <label htmlFor="sm-category">Scenario Type *</label>
                <select
                  id="sm-category"
                  value={form.scenario_category}
                  onChange={(e) => updateField('scenario_category', e.target.value)}
                >
                  <option value="" disabled>Select a scenario type</option>
                  {SCENARIO_CATEGORIES.map((t) => (
                    <option key={t} value={t}>
                      {SCENARIO_CATEGORY_LABELS[t]}
                    </option>
                  ))}
                </select>
                {formErrors.scenario_category && (
                  <span className="sm-field-error">{formErrors.scenario_category}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="sm-reading-time">Reading time (minutes) *</label>
                <input
                  id="sm-reading-time"
                  type="number"
                  min={1}
                  max={60}
                  value={form.reading_time_minutes}
                  onChange={(e) => updateField('reading_time_minutes', e.target.value)}
                />
                {formErrors.reading_time_minutes && (
                  <span className="sm-field-error">{formErrors.reading_time_minutes}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="sm-context">Context *</label>
              <textarea
                id="sm-context"
                maxLength={5000}
                rows={3}
                value={form.context}
                onChange={(e) => updateField('context', e.target.value)}
              />
              {formErrors.context && <span className="sm-field-error">{formErrors.context}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="sm-guidelines">Guidelines</label>
              <textarea
                id="sm-guidelines"
                rows={2}
                value={form.guidelines}
                onChange={(e) => updateField('guidelines', e.target.value)}
              />
              {formErrors.guidelines && (
                <span className="sm-field-error">{formErrors.guidelines}</span>
              )}
            </div>

            {/* Persona */}
            <div className="sm-form-subsection">
              <h4>Persona</h4>
              <div className="sm-form-row">
                <div className="form-group">
                  <label htmlFor="sm-persona-name">Name *</label>
                  <input
                    id="sm-persona-name"
                    type="text"
                    value={form.persona.name}
                    onChange={(e) => updatePersonaField('name', e.target.value)}
                  />
                  {formErrors['persona.name'] && (
                    <span className="sm-field-error">{formErrors['persona.name']}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="sm-persona-emotion">Emotional state</label>
                  <input
                    id="sm-persona-emotion"
                    type="text"
                    value={form.persona.emotional_state}
                    onChange={(e) => updatePersonaField('emotional_state', e.target.value)}
                  />
                </div>
              </div>
              <StringListEditor
                label="Personality traits"
                items={form.persona.personality}
                onChange={(items) => updatePersonaField('personality', items)}
                placeholder="e.g. impatient"
              />
              <StringListEditor
                label="Objectives"
                items={form.persona.objectives}
                onChange={(items) => updatePersonaField('objectives', items)}
                placeholder="e.g. resolve billing dispute"
              />
            </div>

            <StringListEditor
              label="Prohibited behaviours"
              items={form.prohibited_behaviours}
              onChange={(items) => updateField('prohibited_behaviours', items)}
              placeholder="e.g. do not make promises"
            />
            <StringListEditor
              label="Reference transcripts"
              items={form.reference_transcripts}
              onChange={(items) => updateField('reference_transcripts', items)}
              placeholder="Paste a reference transcript line"
            />

            {/* Questions */}
            <div className="sm-form-subsection">
              <div className="sm-section-head">
                <h4>Questions ({form.questions.length}/{MAX_QUESTIONS})</h4>
                <button
                  type="button"
                  className="btn-chip-add"
                  onClick={addQuestion}
                  disabled={form.questions.length >= MAX_QUESTIONS}
                >
                  + Add Question
                </button>
              </div>
              {formErrors.questions && (
                <span className="sm-field-error">{formErrors.questions}</span>
              )}
              {form.questions.map((q, idx) => (
                <div className="sm-question-item" key={idx}>
                  <div className="sm-question-head">
                    <span className="sm-question-order">#{q.order}</span>
                    <div className="sm-question-controls">
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label={`Move question ${q.order} up`}
                        onClick={() => moveQuestion(idx, -1)}
                        disabled={idx === 0}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label={`Move question ${q.order} down`}
                        onClick={() => moveQuestion(idx, 1)}
                        disabled={idx === form.questions.length - 1}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-icon-danger"
                        aria-label={`Remove question ${q.order}`}
                        onClick={() => removeQuestion(idx)}
                        disabled={form.questions.length <= MIN_QUESTIONS}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <textarea
                    rows={2}
                    maxLength={1000}
                    value={q.question_text}
                    aria-label={`Question ${q.order} text`}
                    onChange={(e) => updateQuestionText(idx, e.target.value)}
                  />
                  {qErrors[idx] && <span className="sm-field-error">{qErrors[idx]}</span>}
                </div>
              ))}
            </div>

            <div className="sm-form-actions">
              <button type="button" className="btn-cancel" onClick={() => setView('list')}>
                Cancel
              </button>
              <button type="submit" className="btn-action" disabled={submitting}>
                {submitting
                  ? 'Saving…'
                  : view === 'edit'
                  ? 'Save Changes'
                  : 'Create Scenario'}
              </button>
            </div>
          </form>
        )}
      </div>
    );
  };

  const renderDirectQuestions = () => (
    <div className="mgmt-card sm-dq-card">
      <h3>Direct Questions</h3>

      {createDisabled ? (
        <p className="sm-unclassified-note">
          Select Training or Hiring to add new items.
        </p>
      ) : (
        <form className="sm-dq-add" onSubmit={handleAddDirectQuestion}>
          <label htmlFor="sm-dq-new">Add a direct question</label>
          <textarea
            id="sm-dq-new"
            rows={2}
            maxLength={2000}
            value={dqDraft}
            onChange={(e) => setDqDraft(e.target.value)}
            placeholder="Enter the direct question text"
          />
          {dqDraftError && <span className="sm-field-error">{dqDraftError}</span>}
          <div className="form-group sm-dq-purpose">
            <label htmlFor="sm-dq-purpose">Purpose *</label>
            <select
              id="sm-dq-purpose"
              value={directFormPurpose}
              onChange={(e) => {
                setDirectFormPurpose(e.target.value);
                if (e.target.value) setDirectFormPurposeError('');
              }}
            >
              <option value="">— Select a purpose —</option>
              {PURPOSES.map((p) => (
                <option key={p} value={p}>
                  {PURPOSE_LABELS[p] || p}
                </option>
              ))}
            </select>
            {directFormPurposeError && (
              <span className="sm-field-error">{directFormPurposeError}</span>
            )}
          </div>
          <div className="sm-form-actions">
            <button type="submit" className="btn-action">
              Add Question
            </button>
          </div>
        </form>
      )}

      {dqLoading ? (
        <p className="mgmt-loading">Loading direct questions…</p>
      ) : dqError ? (
        <div className="sm-inline-error">
          <span>{dqError}</span>
          <button
            className="btn-back"
            onClick={() => loadDirectQuestions(selectedProcess, selectedPurpose)}
          >
            Retry
          </button>
        </div>
      ) : directQuestions.length === 0 ? (
        <p className="mgmt-empty">No direct questions yet for this process and purpose.</p>
      ) : (
        <div className="sm-dq-list">
          {directQuestions.map((dq) => (
            <div className="sm-dq-row" key={dq.question_id}>
              <span className="sm-dq-id">{dq.question_id}</span>
              {dqEditingId === dq.question_id ? (
                <div className="sm-dq-edit">
                  <textarea
                    rows={2}
                    maxLength={2000}
                    value={dqEditText}
                    aria-label={`Edit direct question ${dq.question_id}`}
                    onChange={(e) => setDqEditText(e.target.value)}
                  />
                  <div className="form-group sm-dq-purpose">
                    <label htmlFor={`sm-dq-edit-purpose-${dq.question_id}`}>Purpose *</label>
                    <select
                      id={`sm-dq-edit-purpose-${dq.question_id}`}
                      value={dqEditPurpose}
                      onChange={(e) => {
                        setDqEditPurpose(e.target.value);
                        if (e.target.value) setDqEditPurposeError('');
                      }}
                    >
                      <option value="">— Select a purpose —</option>
                      {PURPOSES.map((p) => (
                        <option key={p} value={p}>
                          {PURPOSE_LABELS[p] || p}
                        </option>
                      ))}
                    </select>
                    {dqEditPurposeError && (
                      <span className="sm-field-error">{dqEditPurposeError}</span>
                    )}
                  </div>
                  <div className="sm-dq-edit-actions">
                    <button className="btn-cancel" onClick={cancelEditDirectQuestion}>
                      Cancel
                    </button>
                    <button
                      className="btn-action"
                      onClick={() => saveEditDirectQuestion(dq.question_id)}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <span className="sm-dq-text">{dq.question_text}</span>
                  <div className="sm-dq-actions">
                    <button className="btn-back" onClick={() => startEditDirectQuestion(dq)}>
                      Edit
                    </button>
                    <button className="btn-remove" onClick={() => setDqDeleteTarget(dq)}>
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ---------------- Top-level render ---------------- */

  return (
    <div className="mgmt-content sm-content">
      <h2 className="page-title">Scenario Management</h2>

      {message && <div className="mgmt-success">{message}</div>}
      {error && <div className="mgmt-error">{error}</div>}

      {renderFilterBar()}

      {!ready ? (
        <div className="mgmt-card">
          <p className="mgmt-empty">
            Select both a process and a purpose above to manage its scenarios and direct
            questions.
          </p>
        </div>
      ) : (
        <>
          {view === 'list' && renderScenarioList()}
          {view === 'detail' && renderDetail()}
          {(view === 'create' || view === 'edit') && renderForm()}

          {/* Direct questions always visible under the list view */}
          {view === 'list' && renderDirectQuestions()}
        </>
      )}

      {/* Delete scenario confirmation */}
      {deleteTarget && (
        <ConfirmDialog
          title="Delete scenario"
          message={`Are you sure you want to delete "${
            deleteTarget.title || deleteTarget.scenario_id
          }"? This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={confirmDeleteScenario}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Delete direct question confirmation */}
      {dqDeleteTarget && (
        <ConfirmDialog
          title="Delete direct question"
          message={`Are you sure you want to delete direct question "${dqDeleteTarget.question_id}"? This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={confirmDeleteDirectQuestion}
          onCancel={() => setDqDeleteTarget(null)}
        />
      )}
    </div>
  );
}
